/**
 * Console summary row — a one-line report of what the page logged.
 *
 * Surfaced in the chrome rather than behind a panel because a page that is
 * throwing is the single most useful thing to know about it, and a count the
 * user has to go looking for is a count nobody reads.
 *
 * @module BrowserConsoleRow
 */
import { ActivityIcon } from "lucide-react";

import type { BrowserChromeProps } from "./BrowserChrome";

export function BrowserPreviewConsoleSummaryRow(props: BrowserChromeProps) {
  if (!props.activeTab || !props.activeConsoleLogsMeta || !props.activeConsoleSummary) {
    return null;
  }
  return (
    <BrowserConsoleSummaryBar
      meta={props.activeConsoleLogsMeta}
      summary={props.activeConsoleSummary}
    />
  );
}

interface BrowserConsoleSummaryBarProps {
  readonly meta: {
    readonly capturedAt: string;
    readonly count: number;
  };
  readonly summary: {
    readonly count: number;
    readonly highestLevel: string | null;
    readonly latestMessage: string | null;
  };
}

function BrowserConsoleSummaryBar(props: BrowserConsoleSummaryBarProps) {
  return (
    <div className="border-border border-t px-3 py-1.5 text-[11px] text-muted-foreground">
      {props.meta.count === 0 ? (
        <span>No console logs captured for this tab.</span>
      ) : (
        <div className="flex min-w-0 items-center gap-1.5">
          <ActivityIcon className="size-3 shrink-0" />
          <span className="shrink-0">
            Console logs: {props.summary.count}
            {props.summary.highestLevel ? ` (${props.summary.highestLevel})` : ""}
          </span>
          {props.summary.latestMessage ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="truncate">{props.summary.latestMessage}</span>
              <span className="shrink-0">
                {new Date(props.meta.capturedAt).toLocaleTimeString()}
              </span>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
